import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

/** The open pane lives on the URL, e.g. `/sessions/abc?pane=diff&paneId=att_123`. */
export const WORKSPACE_PANE_QUERY_PARAM = "pane";
export const WORKSPACE_PANE_ID_QUERY_PARAM = "paneId";
export const WORKSPACE_PANE_TYPES = ["diff", "canvas"] as const;
export type WorkspacePaneType = (typeof WORKSPACE_PANE_TYPES)[number];
export interface WorkspacePaneState {
  type: WorkspacePaneType;
  id?: string;
}
interface PendingPaneState {
  pane: WorkspacePaneState | null;
  url: string;
  sequence: number;
}
function first(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}
function pathnameOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}
export function parseWorkspacePaneType(value: string | string[] | undefined): WorkspacePaneType | null {
  const raw = first(value);
  return WORKSPACE_PANE_TYPES.includes(raw as WorkspacePaneType) ? raw as WorkspacePaneType : null;
}

/** URL-backed pane state with an optimistic value while Next's shallow route settles. */
export function useWorkspacePaneUrlState() {
  const $ = _c(24);
  const router = useRouter();
  const [localPane, setLocalPane] = useState(null);
  const [pending, setPending] = useState();
  const sequence = useRef(0);
  let t0;
  if ($[0] !== router) {
    t0 = router ? parseWorkspacePaneType(router.query[WORKSPACE_PANE_QUERY_PARAM]) : null;
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeType = t0;
  let t1;
  if ($[2] !== routeType || $[3] !== router) {
    t1 = router && routeType !== null ? first(router.query[WORKSPACE_PANE_ID_QUERY_PARAM]) ?? null : null;
    $[2] = routeType;
    $[3] = router;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const routeId = t1;
  const routePathname = router ? pathnameOf(router.asPath) : null;
  const pendingSettled = pending !== undefined && ((pending.pane === null ? routeType === null : routeType === pending.pane.type && routeId === (pending.pane.id ?? null)) || routePathname !== null && routePathname !== pathnameOf(pending.url));
  if (pendingSettled) {
    setPending(undefined);
  }
  const activePending = pendingSettled ? undefined : pending;
  let t2;
  let t3;
  if ($[5] !== router) {
    t2 = () => {
      if (!router?.events) {
        return;
      }
      const settle = () => setPending(undefined);
      const settleUrl = url => {
        setPending(current => current?.url === url ? undefined : current);
      };
      const settleError = (_error, url_0) => settleUrl(url_0);
      router.events.on("routeChangeComplete", settleUrl);
      router.events.on("routeChangeError", settleError);
      window.addEventListener("popstate", settle);
      return () => {
        router.events.off("routeChangeComplete", settleUrl);
        router.events.off("routeChangeError", settleError);
        window.removeEventListener("popstate", settle);
      };
    };
    t3 = [router];
    $[5] = router;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== activePending || $[9] !== localPane || $[10] !== routeId || $[11] !== routeType || $[12] !== router) {
    t4 = router ? activePending ? activePending.pane : routeType === null ? null : {
      type: routeType,
      id: routeId ?? undefined
    } : localPane;
    $[8] = activePending;
    $[9] = localPane;
    $[10] = routeId;
    $[11] = routeType;
    $[12] = router;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const effective = t4;
  const type = effective?.type ?? null;
  const id = effective?.id ?? null;
  let t5;
  if ($[14] !== router) {
    t5 = (next, options) => {
      if (!router) {
        setLocalPane(next);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const openInUrl = parseWorkspacePaneType(params.get(WORKSPACE_PANE_QUERY_PARAM) ?? undefined) !== null;
      if (next === null) {
        params.delete(WORKSPACE_PANE_QUERY_PARAM);
        params.delete(WORKSPACE_PANE_ID_QUERY_PARAM);
      } else {
        params.set(WORKSPACE_PANE_QUERY_PARAM, next.type);
        if (next.id === undefined) {
          params.delete(WORKSPACE_PANE_ID_QUERY_PARAM);
        } else {
          params.set(WORKSPACE_PANE_ID_QUERY_PARAM, next.id);
        }
      }
      const query = params.toString();
      const url_1 = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      const nextSequence = sequence.current = sequence.current + 1;
      setPending({
        pane: next,
        url: url_1,
        sequence: nextSequence
      });
      const navigate = !openInUrl && next !== null && !options?.replace ? router.push(url_1, undefined, {
        shallow: true,
        scroll: false
      }) : router.replace(url_1, undefined, {
        shallow: true,
        scroll: false
      });
      Promise.resolve(navigate).then(completed => {
        if (completed) {
          return;
        }
        setPending(current_0 => current_0?.sequence === nextSequence ? undefined : current_0);
      }).catch(() => {
        setPending(current_1 => current_1?.sequence === nextSequence ? undefined : current_1);
      });
    };
    $[14] = router;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const setPane = t5;
  let t6;
  if ($[16] !== setPane || $[17] !== type) {
    t6 = pane => setPane(type === null ? pane : null);
    $[16] = setPane;
    $[17] = type;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const togglePane = t6;
  let t7;
  if ($[19] !== id || $[20] !== setPane || $[21] !== togglePane || $[22] !== type) {
    t7 = {
      type,
      id,
      setPane,
      togglePane
    };
    $[19] = id;
    $[20] = setPane;
    $[21] = togglePane;
    $[22] = type;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}